import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtSize } from "./backupFormat";

// Ações por arquivo do Cofre de Backups: download autenticado, excluir (unitário/lote), retry.
export function useBackupActions({ load, canDeleteBackup, rows }) {
  const [deleting, setDeleting] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const retryBackup = async (f) => {
    if (retrying) return;
    setRetrying(f.filename);
    try {
      const r = await api.post(`/backups/${encodeURIComponent(f.filename)}/retry`);
      const size = ((r.data.size_bytes || 0) / 1024).toFixed(1);
      toast.success(`Backup reprocessado: ${size} KB${r.data.storage_uploaded ? " · S3 ✓" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry falhou");
    } finally { setRetrying(null); }
  };

  const toggleSelect = (filename) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename); else next.add(filename);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const selectable = canDeleteBackup ? rows : [];
    if (selectable.length && selectable.every((f) => selected.has(f.filename))) setSelected(new Set());
    else setSelected(new Set(selectable.map((f) => f.filename)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const list = Array.from(selected);
    if (!list.length) return;
    if (!window.confirm(
      `🗑️ EXCLUIR ${list.length} BACKUP(S)\n\n` +
      list.slice(0, 8).map((n) => `• ${n}`).join("\n") +
      (list.length > 8 ? `\n… e mais ${list.length - 8}` : "") +
      `\n\nEsta ação apaga os arquivos do disco E remove do histórico. Não pode ser desfeita.\n\n` +
      `Deseja continuar?`
    )) return;
    setBulkDeleting(true);
    try {
      const r = await api.post(`/backups/bulk-delete`, { filenames: list });
      const { deleted = [], failed = [], total_bytes = 0 } = r.data || {};
      const mb = (total_bytes / (1024 * 1024)).toFixed(2);
      if (deleted.length) toast.success(`${deleted.length} backup(s) removido(s) · ${mb} MB liberados`);
      if (failed.length) {
        console.warn("Falhas no bulk delete:", failed);
        toast.warning(`${failed.length} arquivo(s) falharam — veja o console para detalhes`);
      }
      setSelected(new Set());
      load();
    } catch (e) {
      console.error("Falha no bulk delete", e);
      toast.error(e?.response?.data?.detail || "Falha ao excluir em lote");
    } finally { setBulkDeleting(false); }
  };

  const deleteBackup = async (f) => {
    if (!canDeleteBackup) {
      toast.error("Sem permissão para excluir backups — peça ao admin (matriz: Backup / E)");
      return;
    }
    if (!window.confirm(
      `🗑️ EXCLUIR BACKUP\n\nArquivo: ${f.filename}\nEmpresa: ${f.empresa_nome || "—"}\n` +
      `Tamanho: ${fmtSize(f.size_bytes)}\n\n` +
      `Esta ação apaga o arquivo do disco E remove do histórico. Não pode ser desfeita.\n\n` +
      `Deseja continuar?`
    )) return;
    setDeleting(f.filename);
    try {
      await api.delete(`/backups/${encodeURIComponent(f.filename)}`);
      toast.success(`Backup "${f.filename}" removido`);
      load();
    } catch (e) {
      console.error("Falha ao excluir backup", f.filename, e);
      toast.error(e?.response?.data?.detail || "Falha ao excluir backup");
    } finally { setDeleting(null); }
  };

  const download = async (filename) => {
    try {
      // fetch com credentials para streaming binário (nunca via URL pública)
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(`${base}/api/backups/${encodeURIComponent(filename)}/download`, { credentials: "include" });
      if (!resp.ok) throw new Error("Falha no download");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Baixando ${filename}`);
    } catch (e) {
      console.debug("download de backup falhou", e);
      toast.error("Erro no download — verifique sua sessão");
    }
  };

  return { deleting, retrying, selected, bulkDeleting, retryBackup, toggleSelect, toggleSelectAll, clearSelection, bulkDelete, deleteBackup, download };
}
